import { Textbox, Rect, Canvas, Shadow } from "fabric";
import type { TextColor, TextOptions } from "../tools/TextTool";

const padding = 6;

class TextObject extends Textbox {
  private backgroundRect: Rect | null = null;

  private maxLength: number | null = null;

  private hasEnteredEditMode = false;

  constructor(text: string, options: TextOptions) {
    const width = text.length * (options.fontSize / 2.2);

    super(text, {
      fill: options.fill,
      fontSize: options.fontSize,
      fontFamily: "Arial",
      selectable: true,
      padding: 6,
      width,
      stroke: "#000000",
      strokeWidth: 0.2,
      splitByGrapheme: true,
      textAlign: "left",
      shadow: new Shadow({
        color: "rgba(0, 0, 0, 0.3)",
        blur: 1.5,
        offsetX: 0.5,
        offsetY: 0.5,
        affectStroke: true,
      }),
    });

    this.maxLength = options.maxLength;

    this.setControlsVisibility({
      mt: false, // middle top
      mb: false, // middle bottom
      ml: true, // middle left
      mr: true, // middle right
      bl: true, // bottom left
      br: true, // bottom right
      tl: true, // top left
      tr: true, // top right
      mtr: false, // middle top rotate
    });

    this.on("moving", this.updateBackground.bind(this));
    this.on("scaling", this.updateBackground.bind(this));
    this.on("resizing", this.updateBackground.bind(this));
    this.on("changed", this.handleTextChange.bind(this));
    this.on("editing:entered", this.handleEditingEntered.bind(this));
  }

  private handleTextChange() {
    if (this.text && this.maxLength && this.text.length > this.maxLength) {
      this.set("text", this.text.slice(0, this.maxLength));
      if (this.hiddenTextarea) {
        this.hiddenTextarea.value = this.text;
      }
      this.canvas?.requestRenderAll();
    }

    this.updateBackground();
  }

  private handleEditingEntered() {
    if (!this.hasEnteredEditMode) {
      this.set("text", "");

      const { hiddenTextarea } = this;
      if (hiddenTextarea) {
        hiddenTextarea.value = "";
      }

      this.hasEnteredEditMode = true;
    }

    if (this.canvas) {
      const canvasWidth = this.canvas.width;

      this.set({
        maxWidth: canvasWidth - this.left - 10,
      });

      this.canvas.requestRenderAll();
    }
  }

  public init(canvas: Canvas) {
    this.canvas = canvas;
    this.canvas.add(this);
    this.canvas.renderAll();

    this.canvas.viewportCenterObject(this);

    this.createBackground();
    this.canvas.bringObjectForward(this);
  }

  private createBackground() {
    if (!this.canvas) return;

    this.backgroundRect = new Rect({
      fill: "rgba(0, 0, 0, 0.4)",
      rx: 16,
      ry: 16,
      selectable: false,
      evented: false,
    });

    this.updateBackground();

    this.canvas.add(this.backgroundRect);
  }

  private updateBackground() {
    if (!this.backgroundRect) return;

    this.backgroundRect.set({
      left: this.left - padding * this.scaleX,
      top: this.top - padding * this.scaleY,
      angle: this.angle,
      width: this.width + padding * 2,
      height: this.height + padding * 2,
      scaleX: this.scaleX,
      scaleY: this.scaleY,
    });
    this.canvas?.requestRenderAll();
  }

  public updateFill(fill: TextColor) {
    this.set({ fill });
  }
}

export default TextObject;
